import { httpsCallable } from 'firebase/functions';

export function buildOsmPoiId(osmType, osmId) {
  return `osm_${osmType || 'node'}_${osmId}`;
}

export function resolvePoiCategoryFromTags(tags = {}) {
  if (['hotel', 'motel', 'hostel', 'guest_house', 'resort'].includes(tags.tourism)) return 'hotel';
  if (tags.amenity === 'fuel') return 'gas_station';
  if (tags.aeroway === 'aerodrome') return 'airport';
  if (tags.railway === 'station' || tags.railway === 'halt') return 'train_station';
  if (tags.amenity === 'bus_station' || tags.public_transport === 'station') return 'bus_station';
  if (tags.highway === 'rest_area' || tags.highway === 'services') return 'rest_stop';
  if ((tags.amenity === 'parking' && tags.parking === 'overnight') || tags.tourism === 'caravan_site') return 'rv_parking';
  if (tags.barrier === 'border_control') return 'border_checkpoint';
  if (tags.tourism === 'museum') return 'museum';
  if (tags.leisure === 'park') return 'park';
  if (tags.leisure === 'fitness_centre' || tags.leisure === 'sports_centre') return 'gym';
  if (tags.leisure === 'spa') return 'spa';
  if (['clinic', 'doctors', 'urgent_care'].includes(tags.amenity)) return 'clinic';
  if (tags.amenity === 'massage' || tags.shop === 'massage') return 'massage_parlor';
  if (tags.shop === 'hairdresser' || tags.shop === 'barber') return 'barbershop';
  if (tags.sport === 'climbing') return 'rock_climbing_gym';
  if (tags.leisure === 'fishing') return 'fishing_destination';
  if (tags.leisure === 'golf_course') return 'golf_course';
  return null;
}

export function poiRecordToMapFeature(poi) {
  if (!poi || typeof poi.lat !== 'number' || typeof poi.lng !== 'number') return null;
  const category = poi.category;
  if (!category) return null;
  const name = (poi.name || '').trim();
  if (!name || name.length < 2) return null;
  const id = String(poi.id || buildOsmPoiId('node', poi.osmId));
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [poi.lng, poi.lat] },
    properties: {
      id,
      name,
      category,
      address: poi.address || '',
      source: 'firestore',
    },
  };
}

export function overpassElementToMapFeature(element, activeCategories) {
  const lat = element.lat ?? element.center?.lat;
  const lng = element.lon ?? element.center?.lon;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  const name = (element.tags?.name || '').trim();
  if (!name || name.length < 2) return null;
  const category = resolvePoiCategoryFromTags(element.tags || {});
  if (!category || !activeCategories.includes(category)) return null;
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {
      id: buildOsmPoiId(element.type || 'node', element.id),
      name,
      category,
      address: element.tags?.['addr:street']
        ? `${element.tags['addr:street']} ${element.tags['addr:housenumber'] || ''}`.trim()
        : '',
      source: 'overpass',
    },
  };
}

/** Merge incoming features into grouped map; Firestore entries win on duplicate ids. */
export function mergeGroupedPoiFeatures(targetGrouped, incomingGrouped, { preferExisting = true } = {}) {
  const result = { ...targetGrouped };
  Object.entries(incomingGrouped || {}).forEach(([category, features]) => {
    if (!Array.isArray(features) || features.length === 0) return;
    const existing = result[category] ? [...result[category]] : [];
    const seen = new Set(existing.map((f) => f.properties?.id).filter(Boolean));
    features.forEach((feature) => {
      const id = feature?.properties?.id;
      if (!id) return;
      if (seen.has(id)) {
        if (!preferExisting) {
          const idx = existing.findIndex((f) => f.properties?.id === id);
          if (idx >= 0) existing[idx] = feature;
        }
        return;
      }
      seen.add(id);
      existing.push(feature);
    });
    result[category] = existing;
  });
  return result;
}

export function groupedFeaturesFromPoiRecords(pois, activeCategories) {
  const grouped = {};
  (pois || []).forEach((poi) => {
    const feature = poiRecordToMapFeature(poi);
    if (!feature || !activeCategories.includes(feature.properties.category)) return;
    const category = feature.properties.category;
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(feature);
  });
  return grouped;
}

export function groupedFeaturesFromOverpassElements(elements, activeCategories) {
  const grouped = {};
  (elements || []).forEach((element) => {
    const feature = overpassElementToMapFeature(element, activeCategories);
    if (!feature) return;
    const category = feature.properties.category;
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(feature);
  });
  return grouped;
}

export async function queryPoisFromFirestore(functionsInstance, bbox, categories, limit = 1200) {
  const queryPoisByBBox = httpsCallable(functionsInstance, 'queryPoisByBBox');
  const result = await queryPoisByBBox({
    bbox: {
      south: bbox.s,
      west: bbox.w,
      north: bbox.n,
      east: bbox.e,
    },
    categories,
    limit,
  });
  return result?.data?.pois || [];
}

export async function fetchOverpassPoiElements(bbox, activeCategories, selectorsByCategory) {
  const parts = [];
  activeCategories.forEach((cat) => {
    const selectors = selectorsByCategory[cat] || [];
    selectors.forEach((sel) => {
      parts.push(`node${sel}(${bbox.s},${bbox.w},${bbox.n},${bbox.e});`);
      parts.push(`way${sel}(${bbox.s},${bbox.w},${bbox.n},${bbox.e});`);
    });
  });
  if (parts.length === 0) return [];

  const query = `[out:json][timeout:30];\n(\n${parts.join('\n')}\n);\nout center;`;
  const res = await fetch('https://overpass-api.de/api/interpreter', {
    method: 'POST',
    body: query,
  });
  if (!res.ok) throw new Error(`Overpass HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.elements) ? json.elements : [];
}

/**
 * Live Overpass fetch for the active travel categories along a route polyline (instead of a
 * viewport bbox), so a whole day's corridor loads at once at any zoom.
 * @param {Array<[number, number]>} sampledCoords [lng, lat] points along the route
 */
export async function fetchOverpassPoiElementsAlongRoute(sampledCoords, radiusMiles, activeCategories, selectorsByCategory) {
  if (!Array.isArray(sampledCoords) || sampledCoords.length === 0) return [];
  const pairs = sampledCoords
    .map(([lng, lat]) => `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`)
    .join(',');
  const around = `(around:${Math.round(Number(radiusMiles) * 1609.344)},${pairs})`;
  const parts = [];
  activeCategories.forEach((cat) => {
    (selectorsByCategory[cat] || []).forEach((sel) => {
      parts.push(`node${sel}${around};`);
      parts.push(`way${sel}${around};`);
    });
  });
  if (parts.length === 0) return [];
  const query = `[out:json][timeout:45];\n(\n${parts.join('\n')}\n);\nout center 3000;`;
  const res = await fetch('https://overpass-api.de/api/interpreter', { method: 'POST', body: query });
  if (!res.ok) throw new Error(`Overpass HTTP ${res.status}`);
  const json = await res.json();
  return Array.isArray(json.elements) ? json.elements : [];
}

/** Escape user text for an Overpass "~" regex inside a double-quoted string. */
function overpassNameRegex(text) {
  // Regex-escape first, then QL-escape the result (QL strings need "\\" for a literal backslash).
  const regex = String(text || '').trim().replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
  return regex.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Live Overpass name search for travel places along a route: every travel-category selector,
 * filtered by name (case-insensitive) and by `around` the route polyline.
 * @param {Array<[number, number]>} sampledCoords [lng, lat] points along the route
 * @returns {Promise<object[]>} map features (same shape as overpassElementToMapFeature)
 */
export async function searchOverpassPoisAlongRoute({ query, sampledCoords, radiusMiles, selectorsByCategory }) {
  const name = overpassNameRegex(query);
  if (!name || !Array.isArray(sampledCoords) || sampledCoords.length === 0) return [];
  const pairs = sampledCoords
    .map(([lng, lat]) => `${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}`)
    .join(',');
  const around = `(around:${Math.round(Number(radiusMiles) * 1609.344)},${pairs})`;
  const categories = Object.keys(selectorsByCategory || {});
  const parts = [];
  categories.forEach((cat) => {
    (selectorsByCategory[cat] || []).forEach((sel) => {
      parts.push(`node${sel}["name"~"${name}",i]${around};`);
      parts.push(`way${sel}["name"~"${name}",i]${around};`);
    });
  });
  if (parts.length === 0) return [];
  const body = `[out:json][timeout:30];\n(\n${parts.join('\n')}\n);\nout center 200;`;
  const res = await fetch('https://overpass-api.de/api/interpreter', { method: 'POST', body });
  if (!res.ok) throw new Error(`Overpass HTTP ${res.status}`);
  const json = await res.json();
  const seen = new Set();
  return (Array.isArray(json.elements) ? json.elements : [])
    .map((element) => overpassElementToMapFeature(element, categories))
    .filter((feature) => {
      if (!feature || seen.has(feature.properties.id)) return false;
      seen.add(feature.properties.id);
      return true;
    });
}

export async function persistPoisToFirestore(functionsInstance, bbox, categories) {
  const fetchAndStorePOIs = httpsCallable(functionsInstance, 'fetchAndStorePOIs', { timeout: 180000 });
  const result = await fetchAndStorePOIs({
    bbox: {
      south: bbox.s,
      west: bbox.w,
      north: bbox.n,
      east: bbox.e,
    },
    categories,
  });
  return result?.data || {};
}
